import { AnyAction, configureStore, createListenerMiddleware, ThunkAction, ThunkDispatch, TypedStartListening } from "@reduxjs/toolkit";
import { createLogger } from "redux-logger";
import { saveFilterPrinterIds } from "../helpers/printerFilterStorage";
import settingsMiddleware from "./middlewares/settings.middleware";
import reducers from "./reducers/reducers";
import { initialRootState, RootReducerTypes, setDisplay, setFilterPrinterIds, setOperationMode, setOrderMode } from "./reducers/rootSlice";
import { changeSetting, initialSettingsState, SettingReducerTypes, toggleColourAlerts, toggleSetting, updateColourAlertRange } from "./reducers/settingsSlice";

const STORAGE_REDUX_KEY = "oms-state";

/**load from local storage into redux */
const loadState = () => {
  try {
    const values: Partial<ReduxState> = JSON.parse(localStorage.getItem(STORAGE_REDUX_KEY) ?? "{}");
    return { root: { ...initialRootState, ...values.root }, settings: { ...initialSettingsState, ...values.settings } };
  } catch {
    return { root: initialRootState, settings: initialSettingsState };
  }
};

const listenerMiddleware = createListenerMiddleware();

export const reduxStore = configureStore({
  reducer: reducers,
  preloadedState: loadState() as any,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware()
      .prepend(listenerMiddleware.middleware)
      .concat(settingsMiddleware, ...(process.env.NODE_ENV === "development" ? [createLogger()] : [])),
});

export type AppDispatch = typeof reduxStore.dispatch;
export type ReduxState = ReturnType<typeof reducers>;
export type TypedDispatch = ThunkDispatch<ReduxState, any, AnyAction>;
export type TypedThunk<ReturnType = void> = ThunkAction<ReturnType, ReduxState, unknown, AnyAction>;

type AppStartListening = TypedStartListening<ReduxState, AppDispatch>;
const startAppListening = listenerMiddleware.startListening as AppStartListening;

const getDataToStore = (state: ReduxState) => {
  const data: { root: RootReducerTypes; settings: SettingReducerTypes } = {
    root: {
      operationMode: state.root.operationMode,
      orderModeType: state.root.orderModeType,
      display: state.root.display,
    },
    settings: {
      showCustomerName: state.settings.showCustomerName,
      splitItemsByTable: state.settings.splitItemsByTable,
      showHiddenDockets: state.settings.showHiddenDockets,
      showTableServiceDockets: state.settings.showTableServiceDockets,
      showCounterServiceDockets: state.settings.showCounterServiceDockets,
      showPickupDockets: state.settings.showPickupDockets,
      showVacatedBookingDockets: state.settings.showVacatedBookingDockets,
      singleDocketPerTable: state.settings.singleDocketPerTable,
      colourAlertsForOrderTime: state.settings.colourAlertsForOrderTime,
      useGroupedDocketLayout: state.settings.useGroupedDocketLayout,
    },
  } as { root: RootReducerTypes; settings: SettingReducerTypes };
  return data;
};

/**perisit in local storage after redux update */
startAppListening({
  predicate: (action) => [setOperationMode.type, setOrderMode.type, setDisplay.type, toggleSetting.type, changeSetting.type, updateColourAlertRange.type, toggleColourAlerts.type].includes(action.type),
  effect: (_, listenerApi) => {
    const data = getDataToStore(listenerApi.getState());
    localStorage.setItem(STORAGE_REDUX_KEY, JSON.stringify(data));
  },
});

/**persist selected printers per restaurant */
startAppListening({
  actionCreator: setFilterPrinterIds,
  effect: (action, listenerApi) => {
    saveFilterPrinterIds(listenerApi.getState().root.printers, action.payload);
  },
});
